import {
    ApiWheelSnapshotV2ResSchema,
    ApiWheelExposureReqSchema,
    ApiWheelExposureResSchema,
    type WheelSpinResultV1,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef } from "react";
import { authenticatedApiCall } from "../helpers/api";
import { newestWheelWallet, wheelIdentity } from "../helpers/wheel";

export function useWheels({ exposeVisibleCampaigns = false } = {}) {
    const { state: auth } = useSlice("authSlice");
    const { state, setSlice } = useSlice("wheelSlice");
    const exposed = useRef(new Set<string>());
    const request = useRef<AbortController | null>(null);
    const userId = auth.bootstrapStatus === "ready" ? auth.userId : null;
    const refresh = useCallback(async () => {
        if (!userId) return null;
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        try {
            const { body } = await authenticatedApiCall(
                "/api/v2/wheels",
                ApiWheelSnapshotV2ResSchema,
                { signal: controller.signal },
            );
            if (body.status === "ERROR") throw new Error(body.code);
            if (controller.signal.aborted) return null;
            setSlice((current) => ({
                userId,
                termsVersion: body.termsVersion,
                status: "ready",
                wheels: body.wheels.map((wheel) => {
                    const existing =
                        current.userId === userId
                            ? current.wheels.find(
                                  (w) => w.wheelId === wheel.wheelId,
                              )
                            : null;
                    return existing
                        ? {
                              ...wheel,
                              wallet: newestWheelWallet(
                                  existing.wallet,
                                  wheel.wallet,
                              ),
                          }
                        : wheel;
                }),
            }));
            return body;
        } catch (error) {
            if (!controller.signal.aborted) {
                console.error("[useWheels] snapshot refresh failed", error);
                setSlice({ status: "error" });
            }
            return null;
        }
    }, [userId, setSlice]);
    useEffect(() => {
        void refresh();
        const visible = () => {
            if (!document.hidden) void refresh();
        };
        const timer = window.setInterval(visible, 30_000);
        document.addEventListener("visibilitychange", visible);
        return () => {
            request.current?.abort();
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", visible);
        };
    }, [refresh, state.refreshRequestedRevision]);
    const campaigns = state.userId === userId ? state.wheels : [];
    const nextFreeAt = Math.min(
        ...campaigns.flatMap((w) =>
            w.wallet.nextFreeSpinAt
                ? [Date.parse(w.wallet.nextFreeSpinAt)]
                : [],
        ),
    );
    useEffect(() => {
        if (!Number.isFinite(nextFreeAt)) return;
        const timer = window.setTimeout(
            () => {
                if (!document.hidden) void refresh();
            },
            Math.min(2147483647, Math.max(50, nextFreeAt - Date.now() + 50)),
        );
        return () => window.clearTimeout(timer);
    }, [nextFreeAt, refresh]);
    useEffect(() => {
        if (!exposeVisibleCampaigns || !userId) return;
        const controller = new AbortController();
        const expose = () => {
            if (document.hidden) return;
            for (const wheel of campaigns) {
                if (exposed.current.has(wheel.decisionId)) continue;
                const input = ApiWheelExposureReqSchema.parse(
                    wheelIdentity(wheel),
                );
                void authenticatedApiCall(
                    "/api/v1/wheels/exposures",
                    ApiWheelExposureResSchema,
                    {
                        method: "POST",
                        body: JSON.stringify(input),
                        signal: controller.signal,
                    },
                )
                    .then(({ body }) => {
                        if (body.status === "OK")
                            exposed.current.add(wheel.decisionId);
                    })
                    .catch(() => undefined);
            }
        };
        expose();
        document.addEventListener("visibilitychange", expose);
        return () => {
            controller.abort();
            document.removeEventListener("visibilitychange", expose);
        };
    }, [
        exposeVisibleCampaigns,
        userId,
        campaigns.map((w) => w.decisionId).join(","),
    ]);
    const reconcile = useCallback(
        (result: WheelSpinResultV1) => {
            setSlice((current) =>
                current.userId !== userId
                    ? {}
                    : {
                          wheels: current.wheels.map((w) =>
                              w.wheelId === result.wheelId
                                  ? {
                                        ...w,
                                        wallet: newestWheelWallet(
                                            w.wallet,
                                            result.wallet,
                                        ),
                                    }
                                  : w,
                          ),
                      },
            );
        },
        [setSlice, userId],
    );
    return {
        userId,
        wheels: state.userId === userId ? state.wheels : [],
        termsVersion: state.termsVersion,
        status: state.status,
        refresh,
        reconcile,
    };
}
